import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { Button, Modal, OverlayTrigger, ToggleButton, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { Loading } from "components";
import { DangerAlert } from "components/DangerAlert";
import { Lightbox } from "components/Lightbox";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  changeAnswerVariant,
  getAnswersStatus,
  getAssignmentMaxPoints,
  setPoints,
  setStatus,
} from "store/slices/exercise";
import { getSolutionVariantFromPoints } from "utils/getSolutionVariant";
import { processApiError } from "utils/processApiError";

import { useCreateUnrecognizedAnswerMutation, useValidateAnswerMutation } from "api/generated";
import { isMobile } from "utils/isMobile";

interface Props {
  assignmentId: string;
  data: {
    correctAnswer: string;
    scoreMax: number;
    userAnswer: string;
  };
  index: number;
  type: "success" | "warning" | "danger" | undefined;
  userAssignmentId: string;
  onClick: (index: number, value: boolean) => void;
}

const AnswerValidation: FC<Props> = ({ assignmentId, data, index, type, userAssignmentId }) => {
  const dispatch = useAppDispatch();
  const assignmentMaxPoints = useAppSelector(
    useMemo(() => getAssignmentMaxPoints(userAssignmentId), [userAssignmentId]),
  );
  const answersStatus = useAppSelector(useMemo(() => getAnswersStatus(userAssignmentId), [userAssignmentId]));

  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [validateOnBE, { error: apiMutationError }] = useValidateAnswerMutation();
  const [reportUnrecognizedAnswer] = useCreateUnrecognizedAnswerMutation();

  const { t } = useTranslation(["exercise"]);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processApiError(apiMutationError);
  }, [apiMutationError]);

  if (type !== "danger") return null;

  const reportUnrecognized = async () =>
    await reportUnrecognizedAnswer({
      input: {
        assignmentId,
        userAssignmentId,
        questionNumber: index + 1,
        correctAnswer: data.correctAnswer,
        studentAnswer: data.userAnswer,
        points: data.scoreMax,
        date: new Date().toISOString(),
      },
    }).unwrap();

  const sendToBE = async (index: number) => {
    const copy = [...answersStatus];
    copy[index] = true;

    const response = await validateOnBE({ userAssignmentId, questions: copy });

    if ("data" in response && response.data) {
      dispatch(changeAnswerVariant({ userAssignmentId, index, type: "success" }));

      const points = response.data.claimEvaluation.scoreReal;
      dispatch(setPoints({ userAssignmentId, points }));

      const status = getSolutionVariantFromPoints(points, assignmentMaxPoints);
      dispatch(setStatus({ userAssignmentId, status }));
    }
  };

  const processAnswerReport = async () => {
    try {
      setIsLoading(true);
      // AIDEV-NOTE: Nejdřív reklamace, body až po jejím uložení. Paralelně by selhání
      // reklamace nezastavilo validaci: student by dostal body, dialog by zmizel (type
      // přestane být "danger") a tým by o reklamaci nevěděl. .unwrap() dělá z chyby RTK
      // výjimku, bez něj by catch nikdy nenastal. [TD-334]
      await reportUnrecognized();
      await sendToBE(index);

      setIsLoading(false);
      setIsConfirmModalOpen(false);
    } catch (error) {
      processApiError(error);
      setIsLoading(false);
      setIsError(true);
    }
  };

  const handleConfirm = () => {
    processAnswerReport().catch(console.error);
  };

  const handleClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isMobile() && event.currentTarget.checked) setIsMobileModalOpen(true);
    else setIsConfirmModalOpen(true);
  };

  const renderButton = () => (
    <ToggleButton
      value={1}
      variant={"select"}
      className={"btn-shadow btn-emoji btn-emoji--thin"}
      type="checkbox"
      id={`toggle-validation_${index}`}
      onChange={handleClick}
    >
      {t("btn.validation", { ns: "exercise" })}
    </ToggleButton>
  );

  return (
    <>
      {isMobile() ? (
        renderButton()
      ) : (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`tooltip-answer-validation-${index}`}>
              {t("answerValidation.tooltipText", { ns: "exercise" })}
            </Tooltip>
          }
        >
          {renderButton()}
        </OverlayTrigger>
      )}

      {/* Vysvětlivka nemá nadpis, jen odstavec — jméno dialogu je proto neviditelné.
          Rozhodnuto se zadavatelem 31. 8. 2026. [TD-344] */}
      <Lightbox
        show={isMobileModalOpen}
        onHide={() => {
          setIsMobileModalOpen(false);
        }}
        ariaLabel="Vysvětlení"
      >
        <p className="text-center mb-20px">{t("answerValidation.tooltipText", { ns: "exercise" })}</p>
        <Button
          className="w-100"
          onClick={() => {
            setIsConfirmModalOpen(true);
            setIsMobileModalOpen(false);
          }}
        >
          OK, pokračovat
        </Button>
      </Lightbox>

      {/* AIDEV-NOTE: `ariaLabel`, ne `bodyTitleId` — nadpis se vykresluje jen mimo `isLoading`
          a `isError`, viz podmínku níže; stejný důvod jako u BlacklistOutConfirmModal. [TD-344] */}
      <Lightbox
        show={isConfirmModalOpen}
        onHide={() => {
          setIsConfirmModalOpen(false);
        }}
        ariaLabel={t("answerValidation.heading")}
      >
        {isLoading ? (
          <Loading />
        ) : isError ? (
          <span>Error</span>
        ) : (
          <>
            <h2 className="fs-16 text-center fw-bold mb-20px">{t("answerValidation.heading")}</h2>

            <div className="question question--thin mb-20px">
              <div>{t("answerValidation.yourAnswer")}:</div>
              <div className="mb-3">
                <strong>{data.userAnswer}</strong>
              </div>
              <div>{t("answerValidation.correctAnswer")}:</div>
              <div>
                <strong>{data.correctAnswer}</strong>
              </div>
            </div>

            <p className="text-center mb-20px">{t("answerValidation.description")}</p>

            <Button className={"w-100 mb-2"} onClick={handleConfirm}>
              {t("answerValidation.confirm")}
            </Button>
            <Button
              variant="outline-secondary"
              className={"w-100 text-uppercase"}
              onClick={() => {
                setIsConfirmModalOpen(false);
              }}
            >
              {t("back", { ns: "common" })}
            </Button>

            <DangerAlert className="mt-4">
              <strong>Pozor!</strong> Pokud budeš tuhle funkci zneužívat a opakovaně označovat špatné odpovědi za
              správné, budeme kontaktovat rodiče a je možné, že přijdeš o přístup do aplikace.
            </DangerAlert>
          </>
        )}
      </Lightbox>
    </>
  );
};

export default AnswerValidation;
